import { moduleManifest } from "dws:module-manifest";

// Loads the library's feature modules (js/modules/<name>.js) on demand and installs the stand-ins that make every
// published name callable before its module is there.

const modulePromises = new Map<string, Promise<void>>();
const scriptPromises = new Map<string, Promise<void>>();
const styleNodes = new Map<string, Promise<void>>();
const loadedModules = new Set<string>();

/**
 * The folder the core script was served from (…/js/) and the cache token on its own URL. Modules and vendored
 * scripts are resolved against it, so the library works from _content/DWSBlazorComponentLibrary/ as well as from a
 * copy of its wwwroot (as the demo hosts use), and a new library version gets new module URLs.
 */
function resolveBase(): { baseUrl: string; version: string } {
    const script = document.currentScript as HTMLScriptElement | null;
    const source = script && script.src ? script.src : new URL("_content/DWSBlazorComponentLibrary/js/dws.js", document.baseURI).href;
    const url = new URL(source);
    const version = url.searchParams.get("v") || "";
    url.search = "";
    url.hash = "";
    return { baseUrl: url.href.substring(0, url.href.lastIndexOf("/") + 1), version };
}

const { baseUrl, version } = resolveBase();

function withVersion(relativeUrl: string): string {
    const url = new URL(relativeUrl, baseUrl);
    if (version && !url.searchParams.has("v")) {
        url.searchParams.set("v", version);
    }
    return url.href;
}

function load(name: string): Promise<void> {
    let promise = modulePromises.get(name);
    if (!promise) {
        if (!Object.prototype.hasOwnProperty.call(moduleManifest, name)) {
            return Promise.reject(new Error(`DWS: unknown script module '${name}'.`));
        }

        promise = import(/* webpackIgnore: true */ withVersion(`modules/${name}.js`)).then(() => {
            loadedModules.add(name);
        });
        // A failed load (offline, a deploy that replaced the files) must not stay cached: the next call retries.
        promise.catch(() => modulePromises.delete(name));
        modulePromises.set(name, promise);
    }

    return promise;
}

function loadScript(relativeUrl: string): Promise<void> {
    const href = withVersion(relativeUrl);
    let promise = scriptPromises.get(href);
    if (!promise) {
        promise = new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = href;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                script.remove();
                reject(new Error(`DWS: could not load script '${href}'.`));
            };
            document.head.appendChild(script);
        });
        promise.catch(() => scriptPromises.delete(href));
        scriptPromises.set(href, promise);
    }

    return promise;
}

function loadStyle(relativeUrl: string): Promise<void> {
    const href = withVersion(relativeUrl);
    let promise = styleNodes.get(href);
    if (!promise) {
        promise = new Promise<void>((resolve, reject) => {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = href;
            link.onload = () => resolve();
            link.onerror = () => {
                link.remove();
                reject(new Error(`DWS: could not load stylesheet '${href}'.`));
            };
            document.head.appendChild(link);
        });
        promise.catch(() => styleNodes.delete(href));
        styleNodes.set(href, promise);
    }

    return promise;
}

type IdleWindow = Window & { requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number };

function preload(names: string[] | string, idle?: boolean): void {
    const list = (Array.isArray(names) ? names : String(names).split(","))
        .map(name => name.trim())
        .filter(name => name.length > 0);

    const start = () => {
        for (const name of list) {
            // Preloading is a hint: a failure surfaces when the module is really needed.
            load(name).catch(() => { });
        }
    };

    const idleWindow = window as IdleWindow;
    if (!idle) {
        start();
    } else if (typeof idleWindow.requestIdleCallback === "function") {
        idleWindow.requestIdleCallback(start, { timeout: 5000 });
    } else {
        setTimeout(start, 1500);
    }
}

/** Calls the real member after the module replaced the stand-in, with the namespace as this. */
function forwardMember(moduleName: string, globalName: string, stub: object, member: string) {
    return function (...args: unknown[]): Promise<unknown> {
        return load(moduleName).then(() => {
            const target = window[globalName];
            if (!target || target === stub || typeof target[member] !== "function") {
                throw new Error(`DWS: module '${moduleName}' did not provide ${globalName}.${member}.`);
            }

            return target[member](...args);
        });
    };
}

function installNamespaceStub(moduleName: string, globalName: string): void {
    if (window[globalName] !== undefined) {
        return;
    }

    const members = new Map<string, (...args: unknown[]) => Promise<unknown>>();
    const stub: object = new Proxy({}, {
        get(_target, property) {
            // "then" must stay undefined, or awaiting the namespace object would treat it as a promise.
            if (typeof property !== "string" || property === "then") {
                return undefined;
            }

            let member = members.get(property);
            if (!member) {
                member = forwardMember(moduleName, globalName, stub, property);
                members.set(property, member);
            }

            return member;
        },
        // Blazor resolves "ns.member" with `member in ns` before reading it, so every member must be reported
        // present; without this trap the first .NET call into a namespace failed with "'member' was undefined".
        has(_target, property) {
            return typeof property === "string" && property !== "then";
        }
    });

    window[globalName] = stub;
}

function installFunctionStub(moduleName: string, globalName: string): void {
    if (window[globalName] !== undefined) {
        return;
    }

    const stub = function (...args: unknown[]): Promise<unknown> {
        return load(moduleName).then(() => {
            const target = window[globalName];
            if (typeof target !== "function" || target === stub) {
                throw new Error(`DWS: module '${moduleName}' did not provide ${globalName}.`);
            }

            return target(...args);
        });
    };

    window[globalName] = stub;
}

export function installModuleLoader(): void {
    if (window.dwsModules) {
        return;
    }

    window.dwsModules = {
        load,
        preload,
        loadScript,
        loadStyle,
        baseUrl,
        version,
        loaded: () => Array.from(loadedModules)
    };

    for (const moduleName of Object.keys(moduleManifest)) {
        for (const entry of moduleManifest[moduleName]) {
            if (entry.endsWith(".*")) {
                installNamespaceStub(moduleName, entry.substring(0, entry.length - 2));
            } else {
                installFunctionStub(moduleName, entry);
            }
        }
    }
}
